import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertCircle, ArrowLeft, CheckCircle2, Clock3, History, RefreshCw,
  Snowflake, Thermometer, TriangleAlert,
} from 'lucide-react';
import {
  createHaccpTemperatureStation,
  getHaccpTemperatureHistory,
  getHaccpTemperatureLocations,
  getHaccpTemperatureSummary,
  recordHaccpTemperature,
  getHaccpTemperaturePhoto,
  reviseHaccpTemperatureStation,
} from './useHaccpConfigurations';
import TemperatureCaptureForm from './TemperatureCaptureForm';
import { temperatureLimitLabel } from './temperatureLimits';
import {
  temperatureEquipmentDisplayName,
  temperatureEquipmentForDisplay,
  temperatureEquipmentLabelDescription,
} from './temperatureSetup';
import './haccp.css';

const STATUS = {
  UPCOMING: 'In programma',
  DUE: 'Da registrare',
  OVERDUE: 'In ritardo',
  COMPLETED: 'Registrato',
  NON_COMPLIANT: 'Fuori limite',
};
function localDateMinus(date, days) {
  const value = new Date(`${date}T12:00:00Z`);
  value.setUTCDate(value.getUTCDate() - days);
  return value.toISOString().slice(0, 10);
}

function formatCheckedAt(value, timezone) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    timeZone: timezone,
  }).format(date);
}

function formatLocalDate(value) {
  const date = new Date(`${value}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC',
  }).format(date);
}

const todayStatusLabel = (status) => ({
  DUE: 'Da fare',
  COMPLETED: 'Completato',
  NON_COMPLIANT: 'Anomalia',
}[status] || 'Da fare');

export default function HaccpTemperaturePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [locations, setLocations] = useState([]);
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingId, setSavingId] = useState('');
  const [formError, setFormError] = useState('');
  const [activeForm, setActiveForm] = useState('');
  const [notice, setNotice] = useState('');
  const [photoPreview, setPhotoPreview] = useState('');
  const [stationFormOpen, setStationFormOpen] = useState(false);
  const [stationKind, setStationKind] = useState('FRIDGE');
  const [stationLabel, setStationLabel] = useState('');
  const [stationMinimum, setStationMinimum] = useState('2');
  const [stationMaximum, setStationMaximum] = useState('5');
  const [stationSaving, setStationSaving] = useState(false);
  const [stationError, setStationError] = useState('');
  const [editingStation, setEditingStation] = useState(null);
  useEffect(() => () => { if (photoPreview) URL.revokeObjectURL(photoPreview); }, [photoPreview]);
  const requestedLocationId = searchParams.get('location_id');
  const requestedEquipmentId = searchParams.get('equipment_id');
  const selectedLocation = useMemo(
    () => locations.find((item) => item.location_id === requestedLocationId) || locations[0] || null,
    [locations, requestedLocationId],
  );

  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getHaccpTemperatureLocations();
      if (!response || !Array.isArray(response.locations)) {
        throw new Error('Risposta controlli non valida.');
      }
      setLocations(response.locations);
      if (!response.locations.length) {
        setSummary(null);
        setHistory([]);
        return;
      }
      const selected = response.locations.find((item) => item.location_id === requestedLocationId)
        || response.locations[0];
      if (selected.location_id !== requestedLocationId) {
        setSearchParams({ location_id: selected.location_id }, { replace: true });
      }
      const nextSummary = await getHaccpTemperatureSummary(selected.location_id);
      setSummary(nextSummary);
      if (requestedEquipmentId) {
        const requestedCheck = (nextSummary.items || []).find((item) => (
          (item.equipment_id === requestedEquipmentId || item.station_id === requestedEquipmentId)
          && ['DUE', 'OVERDUE'].includes(item.status)
        ));
        if (requestedCheck) {
          setActiveForm(requestedCheck.standalone
            ? `${requestedCheck.station_id}:daily`
            : `${requestedCheck.equipment_id}:${requestedCheck.scheduled_time_local}`);
          setNotice('Inserisci la temperatura manualmente e, se vuoi, aggiungi una foto.');
        } else {
          setActiveForm('');
          setNotice('Per questa attrezzatura non è previsto un controllo in questo momento.');
        }
        setSearchParams({ location_id: selected.location_id }, { replace: true });
      }
      const nextTo = nextSummary.local_date;
      const nextFrom = dateFrom || localDateMinus(nextTo, 6);
      setDateTo(nextTo);
      setDateFrom(nextFrom);
      const historyResponse = await getHaccpTemperatureHistory(
        selected.location_id, nextFrom, nextTo,
      );
      setHistory(Array.isArray(historyResponse?.records) ? historyResponse.records : []);
    } catch (requestError) {
      setError(requestError?.status === 403
        ? 'Non hai un’assegnazione attiva per questo locale.'
        : 'Controlli temperatura non disponibili. Verifica la connessione e riprova.');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, requestedEquipmentId, requestedLocationId, setSearchParams]);

  useEffect(() => {
    reload();
  }, [reload]);

  const loadLocation = async (locationId, from = dateFrom, to = dateTo) => {
    setLoading(true);
    setError('');
    setActiveForm('');
    try {
      const [nextSummary, historyResponse] = await Promise.all([
        getHaccpTemperatureSummary(locationId),
        from && to ? getHaccpTemperatureHistory(locationId, from, to) : Promise.resolve({ records: [] }),
      ]);
      setSummary(nextSummary);
      setHistory(Array.isArray(historyResponse?.records) ? historyResponse.records : []);
    } catch {
      setError('Impossibile caricare i controlli di questo locale. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const handleLocationChange = (locationId) => {
    setSearchParams({ location_id: locationId });
    const next = locations.find((item) => item.location_id === locationId);
    if (next) loadLocation(locationId);
  };

  const handleHistoryFilter = (field, value) => {
    const nextFrom = field === 'from' ? value : dateFrom;
    const nextTo = field === 'to' ? value : dateTo;
    if (field === 'from') setDateFrom(value);
    else setDateTo(value);
    if (selectedLocation && nextFrom && nextTo && nextFrom <= nextTo) {
      loadLocation(selectedLocation.location_id, nextFrom, nextTo);
    }
  };

  const handleSubmit = async (payload) => {
    if (!selectedLocation || savingId) return;
    const key = payload.station_id
      ? `${payload.station_id}:daily`
      : `${payload.equipment_id}:${payload.scheduled_time_local}`;
    setSavingId(key);
    setFormError('');
    setNotice('');
    try {
      await recordHaccpTemperature(payload);
      setActiveForm('');
      setNotice('Controllo registrato con l’ora del server.');
      await loadLocation(selectedLocation.location_id);
    } catch (requestError) {
      setFormError(requestError?.status === 409
        ? 'Il controllo è già stato registrato o il piano è cambiato. Aggiorna la pagina.'
        : requestError?.status === 422
          ? 'Completa la nota o l’azione correttiva richiesta e riprova.'
          : 'Registrazione non confermata. Controlla lo stato e riprova.');
      return requestError?.status >= 400 && requestError?.status < 500 ? 'DEFINITIVE_ERROR' : 'RETRY';
    } finally {
      setSavingId('');
    }
  };

  const resetStationForm = () => {
    setStationFormOpen(false);
    setEditingStation(null);
    setStationKind('FRIDGE');
    setStationLabel('');
    setStationMinimum('2');
    setStationMaximum('5');
    setStationError('');
  };

  const startStationForm = (station = null) => {
    setEditingStation(station);
    setStationFormOpen(true);
    setStationError('');
    if (station) {
      setStationKind(station.kind);
      setStationLabel(temperatureEquipmentLabelDescription(station.kind, station.label));
      setStationMinimum(station.kind === 'FREEZER'
        ? station.operational_target_min_temperature_c || '-22'
        : station.min_temperature_c || '2');
      setStationMaximum(station.kind === 'FREEZER'
        ? station.operational_target_max_temperature_c || station.max_temperature_c || '-18'
        : station.max_temperature_c || '5');
    } else {
      setStationKind('FRIDGE');
      setStationLabel('');
      setStationMinimum('2');
      setStationMaximum('5');
    }
  };

  const handleStationKindChange = (kind) => {
    setStationKind(kind);
    setStationMinimum(kind === 'FREEZER' ? '-22' : '2');
    setStationMaximum(kind === 'FREEZER' ? '-18' : '5');
  };

  const handleSaveStation = async (event) => {
    event.preventDefault();
    if (!selectedLocation || stationSaving) return;
    const minimum = Number(stationMinimum.replace(',', '.'));
    const maximum = Number(stationMaximum.replace(',', '.'));
    if (!Number.isFinite(minimum) || !Number.isFinite(maximum) || minimum > maximum) {
      setStationError('Controlla i valori minimo e massimo.');
      return;
    }
    setStationSaving(true);
    setStationError('');
    try {
      const values = {
        location_id: selectedLocation.location_id,
        equipment_kind: stationKind,
        label: stationLabel.trim() || null,
        min_temperature_c: stationMinimum.replace(',', '.'),
        max_temperature_c: stationMaximum.replace(',', '.'),
      };
      if (editingStation) {
        await reviseHaccpTemperatureStation(
          editingStation.station_id,
          selectedLocation.location_id,
          {
            expected_revision_id: editingStation.profile_revision_id,
            label: values.label,
            min_temperature_c: values.min_temperature_c,
            max_temperature_c: values.max_temperature_c,
          },
        );
      } else {
        await createHaccpTemperatureStation(values);
      }
      resetStationForm();
      await loadLocation(selectedLocation.location_id);
    } catch (requestError) {
      setStationError(requestError?.status === 409
        ? 'Le impostazioni sono cambiate. Aggiorna la pagina e riprova.'
        : 'Impossibile salvare l’attrezzatura. Verifica i valori e riprova.');
    } finally {
      setStationSaving(false);
    }
  };

  const displayEquipment = temperatureEquipmentForDisplay(summary?.equipment || []);
  const standaloneDisplayNumberById = new Map(
    displayEquipment
      .filter((equipment) => equipment.standalone)
      .map((equipment) => [
        String(equipment.station_id || equipment.equipment_id),
        equipment.displayNumber,
      ]),
  );

  if (loading && !summary && locations.length === 0) {
    return <main className="haccp-page" data-testid="haccp-temperature-page"><div className="haccp-skeleton haccp-skeleton-heading" /><div className="haccp-skeleton haccp-skeleton-row" /></main>;
  }

  return (
    <main className="haccp-page haccp-temperature-page" data-testid="haccp-temperature-page">
      {photoPreview && (
        <div className="haccp-photo-dialog" role="dialog" aria-modal="true" aria-label="Foto originale del controllo" onKeyDown={(event) => { if (event.key === 'Escape') setPhotoPreview(''); }}>
          <div>
            <button type="button" className="haccp-button haccp-button-secondary" autoFocus onClick={() => setPhotoPreview('')}>Chiudi foto</button>
            <img src={photoPreview} alt="Foto originale privata del display" />
          </div>
        </div>
      )}
      <Link to="/haccp" className="haccp-back-link"><ArrowLeft size={16} /> Dashboard HACCP</Link>
      <header className="haccp-page-header">
        <div>
          <div className="haccp-eyebrow"><Thermometer size={15} /> Controlli quotidiani</div>
          <h1>Temperature</h1>
          <p>Un controllo al giorno per ogni attrezzatura autorizzata.</p>
        </div>
        <div className="haccp-header-mark" aria-hidden="true"><Snowflake size={27} /></div>
      </header>

      {error && (
        <section className="haccp-temperature-error-panel" role="alert">
          <AlertCircle size={19} /><span>{error}</span>
          <button type="button" className="haccp-button haccp-button-secondary" onClick={reload}>
            <RefreshCw size={15} /> Riprova
          </button>
        </section>
      )}

      {!error && locations.length === 0 && !loading && (
        <section className="haccp-state-panel" data-testid="haccp-temperature-unavailable">
          <span className="haccp-state-icon"><Thermometer size={21} /></span>
          <div>
            <h2>Temperature non disponibili</h2>
            <p>Questa configurazione non è ancora disponibile. Riprova più tardi.</p>
          </div>
        </section>
      )}

      {locations.length > 0 && selectedLocation && (
        <>
          {locations.length > 1 && (
            <label className="haccp-field haccp-temperature-location">
              <span>Locale assegnato</span>
              <select value={selectedLocation.location_id} onChange={(event) => handleLocationChange(event.target.value)}>
                {locations.map((location) => <option key={location.location_id} value={location.location_id}>{location.location_name}</option>)}
              </select>
            </label>
          )}
          {summary && !summary.operational_available && (
            <section className="haccp-state-panel" role="status">
              <span className="haccp-state-icon"><Thermometer size={21} /></span>
              <div><h2>Controlli non attivi</h2><p>Il piano approvato non contiene ancora tutte le regole per le attrezzature del freddo.</p></div>
            </section>
          )}
          {summary?.operational_available && (
            <>
              {selectedLocation.can_manage_equipment && (
                <section className="haccp-temperature-setup" aria-label="Configurazione attrezzature">
                  {!stationFormOpen ? (
                    <button
                      type="button"
                      className="haccp-button haccp-button-secondary"
                      onClick={() => startStationForm()}
                    >
                      Aggiungi attrezzatura
                    </button>
                  ) : (
                    <form className="haccp-temperature-station-form" onSubmit={handleSaveStation}>
                      <div>
                        <h2>{editingStation ? 'Impostazioni attrezzatura' : 'Nuova attrezzatura'}</h2>
                        <p>Valori aziendali iniziali modificabili, non limiti universali.</p>
                      </div>
                      <label className="haccp-field">
                        <span>Tipo</span>
                        <select
                          value={stationKind}
                          disabled={!!editingStation}
                          onChange={(event) => handleStationKindChange(event.target.value)}
                        >
                          <option value="FRIDGE">Frigorifero</option>
                          <option value="FREEZER">Freezer / Congelatore</option>
                        </select>
                      </label>
                      <label className="haccp-field">
                        <span id="haccp-temperature-station-label-caption">Nome facoltativo</span>
                        <input
                          id="haccp-temperature-station-label"
                          name="temperature-station-label"
                          autoComplete="off"
                          aria-labelledby="haccp-temperature-station-label-caption"
                          maxLength={120}
                          value={stationLabel}
                          onChange={(event) => setStationLabel(event.target.value)}
                          placeholder="Es. Frigo cucina"
                        />
                      </label>
                      <div className="haccp-temperature-value-editor">
                        <label className="haccp-field">
                          <span>{stationKind === 'FREEZER' ? 'Target minimo °C' : 'Temperatura minima °C'}</span>
                          <input
                            inputMode="decimal"
                            required
                            value={stationMinimum}
                            onChange={(event) => setStationMinimum(event.target.value)}
                          />
                        </label>
                        <label className="haccp-field">
                          <span>Temperatura massima °C</span>
                          <input
                            inputMode="decimal"
                            required
                            value={stationMaximum}
                            onChange={(event) => setStationMaximum(event.target.value)}
                          />
                        </label>
                      </div>
                      {stationKind === 'FREEZER' && (
                        <p className="haccp-temperature-instructions">
                          La conformità usa il limite massimo; una temperatura più fredda del target resta conforme.
                        </p>
                      )}
                      {stationError && <p role="alert" className="haccp-temperature-error">{stationError}</p>}
                      <div className="haccp-temperature-form-actions">
                        <button type="button" className="haccp-button haccp-button-secondary" onClick={resetStationForm} disabled={stationSaving}>Annulla</button>
                        <button type="submit" className="haccp-button haccp-button-primary" disabled={stationSaving}>
                          {stationSaving ? 'Salvataggio…' : 'Salva'}
                        </button>
                      </div>
                    </form>
                  )}
                </section>
              )}
              <section className="haccp-temperature-summary" aria-label="Riepilogo controlli di oggi">
                <div><strong>{summary.location_name}</strong><span>{new Intl.DateTimeFormat('it-IT', { dateStyle: 'full', timeZone: summary.timezone }).format(new Date(`${summary.local_date}T12:00:00Z`))}</span></div>
                <div className="haccp-temperature-counts">
                  <span><b>{summary.counts.due}</b> Da fare</span>
                  <span><b>{summary.counts.completed}</b> Completati</span>
                  {summary.items.some((item) => !item.standalone) && (
                    <span className={summary.counts.late ? 'has-alert' : ''}><b>{summary.counts.late}</b> In ritardo</span>
                  )}
                  <span className={summary.counts.non_compliant ? 'has-alert' : ''}><b>{summary.counts.non_compliant}</b> Anomalie</span>
                </div>
              </section>
              {displayEquipment.some((equipment) => equipment.standalone) && (
                <section className="haccp-temperature-grid" aria-label="Attrezzature Temperature">
                  {displayEquipment.filter((equipment) => equipment.standalone).map((equipment) => {
                    const item = summary.items.find((entry) => (
                      entry.standalone && entry.station_id === equipment.station_id
                    ));
                    const key = `${equipment.station_id}:daily`;
                    const status = item?.status || equipment.today_status;
                    return (
                      <article key={equipment.equipment_id} className="haccp-temperature-card" data-testid={`temperature-station-${equipment.station_id}`}>
                        <strong>
                          {temperatureEquipmentDisplayName(equipment.kind, equipment.displayNumber, equipment.label)}
                        </strong>
                        {item && <p className="haccp-temperature-form-limits">{temperatureLimitLabel(item, 'Limiti')}</p>}
                        <p>
                          Ultima temperatura: {equipment.last_temperature_c == null
                            ? 'nessuna registrazione'
                            : `${equipment.last_temperature_c} °C`}
                        </p>
                        {equipment.last_checked_at && (
                          <small>Registrata il {formatCheckedAt(equipment.last_checked_at, summary.timezone)}</small>
                        )}
                        <small>Stato di oggi: {todayStatusLabel(status)}</small>
                        {summary.can_manage_equipment && (
                          <details className="haccp-temperature-settings">
                            <summary>Impostazioni</summary>
                            <button type="button" className="haccp-button haccp-button-secondary" onClick={() => startStationForm(equipment)}>
                              Modifica attrezzatura
                            </button>
                          </details>
                        )}
                        {activeForm === key && item ? (
                          <TemperatureCaptureForm
                            key={key}
                            item={item}
                            summary={summary}
                            saving={savingId === key}
                            error={formError}
                            onCancel={() => { setActiveForm(''); setFormError(''); }}
                            onSubmit={handleSubmit}
                          />
                        ) : item ? (
                          <button type="button" className="haccp-button haccp-button-primary haccp-temperature-record-button" onClick={() => { setFormError(''); setActiveForm(key); }}>
                            <Thermometer size={16} /> Registra temperatura
                          </button>
                        ) : null}
                      </article>
                    );
                  })}
                </section>
              )}
              <button
                type="button"
                className="haccp-button haccp-button-secondary haccp-temperature-history-toggle"
                aria-expanded={historyOpen}
                onClick={() => setHistoryOpen((open) => !open)}
              >
                <History size={16} /> {historyOpen ? 'Nascondi storico controlli' : 'Storico controlli'}
              </button>
              {notice && <p className="haccp-temperature-success" role="status"><CheckCircle2 size={17} /> {notice}</p>}
              {summary.items.some((item) => !item.standalone) && (
              <section className="haccp-temperature-list" aria-label="Controlli previsti oggi">
                <div className="haccp-section-heading">
                  <div><span className="haccp-section-kicker">Oggi</span><h2>Attrezzature</h2></div>
                  <button type="button" className="haccp-icon-refresh" onClick={reload} aria-label="Aggiorna controlli"><RefreshCw size={17} /></button>
                </div>
                {summary.items.filter((item) => !item.standalone).map((item) => {
                  const key = item.standalone
                    ? `${item.station_id}:daily`
                    : `${item.equipment_id}:${item.scheduled_time_local}`;
                  const canRecord = item.standalone
                    ? item.status === 'DUE'
                    : ['DUE', 'OVERDUE'].includes(item.status);
                  const statusLabel = item.standalone && item.status === 'NON_COMPLIANT'
                    ? 'Anomalia'
                    : STATUS[item.status];
                  return (
                    <article className={`haccp-temperature-card is-${item.status.toLowerCase()}`} key={key} data-testid={`temperature-item-${key}`}>
                      <div className="haccp-temperature-card-heading">
                        <span className="haccp-temperature-emblem"><Thermometer size={18} /></span>
                        <div className="haccp-temperature-equipment">
                          <strong>{temperatureEquipmentDisplayName(item.equipment_kind, item.equipment_number, item.equipment_label)}</strong>
                          <small>{item.standalone
                            ? temperatureLimitLabel(item, '')
                            : `Controllo ${item.scheduled_time_local} · ${temperatureLimitLabel(item, '')}`}</small>
                        </div>
                        <span className={`haccp-temperature-status is-${item.status.toLowerCase()}`}>{statusLabel}</span>
                      </div>
                      {item.record && (
                        <div className="haccp-temperature-record">
                          <span><strong>{item.record.temperature_c}°C</strong> · {formatCheckedAt(item.record.checked_at, summary.timezone)}</span>
                          {item.record.late && <small>Registrato in ritardo</small>}
                          {item.record.corrective_action && <p>Azione: {item.record.corrective_action}</p>}
                        </div>
                      )}
                      {!item.standalone && item.status === 'OVERDUE' && <p className="haccp-temperature-overdue"><TriangleAlert size={14} /> Controllo oltre il tempo previsto</p>}
                      {activeForm === key ? (
                        <TemperatureCaptureForm
                          key={key}
                          item={item}
                          summary={summary}
                          saving={savingId === key}
                          error={formError}
                          onCancel={() => { setActiveForm(''); setFormError(''); }}
                          onSubmit={handleSubmit}
                        />
                      ) : canRecord ? (
                        <button type="button" className="haccp-button haccp-button-primary haccp-temperature-record-button" onClick={() => { setFormError(''); setActiveForm(key); }}>
                          <Thermometer size={16} /> Registra temperatura
                        </button>
                      ) : item.standalone && item.status === 'COMPLETED' ? (
                        <p className="haccp-temperature-upcoming"><CheckCircle2 size={14} /> Temperatura di oggi registrata</p>
                      ) : item.standalone && item.status === 'NON_COMPLIANT' ? (
                        <p className="haccp-temperature-overdue"><TriangleAlert size={14} /> Anomalia registrata</p>
                      ) : !item.standalone && item.status === 'UPCOMING' ? (
                        <p className="haccp-temperature-upcoming"><Clock3 size={14} /> Previsto alle {item.scheduled_time_local}</p>
                      ) : null}
                    </article>
                  );
                })}
              </section>
              )}

              {(summary.nonconformities || []).length > 0 && (
                <section className="haccp-temperature-history" aria-label="Non conformità aperte">
                  <h2>Non conformità da verificare</h2>
                  {summary.nonconformities.map((anomaly) => {
                    const rule = anomaly.rule;
                    const verificationItem = rule ? {
                      ...rule,
                      minimum_temperature_c: rule.min_temperature_c,
                      maximum_temperature_c: rule.max_temperature_c,
                      tolerance_c: rule.tolerance_c || '0',
                      scheduled_time_local: rule.check_times_local[0],
                      verification_of: anomaly.check_id,
                    } : null;
                    const key = `verify:${anomaly.check_id}`;
                    return <article className="haccp-temperature-card is-non_compliant" key={key}>
                      <strong>
                        {temperatureEquipmentDisplayName(anomaly.equipment_kind, anomaly.equipment_number, anomaly.equipment_label)} · {anomaly.temperature_c}°C
                      </strong>
                      <p className="haccp-temperature-instructions">{anomaly.instructions}</p>
                      <small>Verifica prevista: {formatCheckedAt(anomaly.verification_due_at, summary.timezone)}</small>
                      {!rule ? <p>Serve una regola attiva per la stessa attrezzatura prima della verifica.</p>
                        : activeForm === key ? <TemperatureCaptureForm
                          key={key} item={verificationItem} summary={summary} saving={!!savingId}
                          error={formError} onCancel={() => setActiveForm('')} onSubmit={handleSubmit}
                        /> : <button type="button" className="haccp-button haccp-button-primary" onClick={() => { setFormError(''); setActiveForm(key); }}>Registra verifica con foto</button>}
                    </article>;
                  })}
                </section>
              )}

              {historyOpen && <section className="haccp-temperature-history" id="haccp-temperature-history" data-testid="haccp-temperature-history">
                <div className="haccp-section-heading">
                  <div><span className="haccp-section-kicker">Registro</span><h2>Storico controlli</h2></div>
                </div>
                <div className="haccp-temperature-date-filters">
                  <label className="haccp-field"><span>Dal</span><input type="date" value={dateFrom} max={dateTo} onChange={(event) => handleHistoryFilter('from', event.target.value)} /></label>
                  <label className="haccp-field"><span>Al</span><input type="date" value={dateTo} min={dateFrom} max={summary.local_date} onChange={(event) => handleHistoryFilter('to', event.target.value)} /></label>
                </div>
                {history.length ? (
                  <div className="haccp-temperature-history-list">
                    {history.map((record) => (
                      <article key={record.id} className="haccp-temperature-history-row">
                        <span className={`haccp-temperature-history-dot ${record.result === 'COMPLIANT' ? 'is-ok' : 'is-alert'}`} />
                        <div>
                          <strong>{formatLocalDate(record.local_date)} · {temperatureEquipmentDisplayName(
                            record.equipment_kind,
                            standaloneDisplayNumberById.get(String(record.stable_equipment_id))
                              ?? record.equipment_number,
                            record.equipment_label,
                          )}</strong>
                          <small>{record.scheduled_time_local ? `${record.scheduled_time_local} · ` : ''}{formatCheckedAt(record.checked_at, summary.timezone)}</small>
                          <small>Operatore: {record.operator_name || 'non disponibile'}</small>
                           <small>{record.photo_path ? '📷 Foto allegata' : '✍️ Manuale'}</small>
                          <details className="haccp-temperature-history-details">
                            <summary>Dettaglio controllo</summary>
                            {record.corrective_action && <small>Azione correttiva: {record.corrective_action}</small>}
                            <small>
                              Revisione {record.profile_revision || record.configuration_version}
                              {' · '}{record.measurement_method === 'PROBE' ? 'Sonda' : 'Display'}
                            </small>
                            {record.photo_path && <button type="button" className="haccp-photo-link" onClick={async () => {
                              try {
                                const blob = await getHaccpTemperaturePhoto(record.photo_path);
                                setPhotoPreview(URL.createObjectURL(blob));
                              } catch { setError('Foto privata non disponibile. Riprova.'); }
                            }}>Apri foto originale privata</button>}
                          </details>
                        </div>
                        <b>{record.temperature_c}°C</b>
                      </article>
                    ))}
                  </div>
                ) : <p className="haccp-temperature-empty-history">Nessun controllo nel periodo selezionato.</p>}
              </section>}
            </>
          )}
        </>
      )}
    </main>
  );
}
